import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { SituationPage } from "@/components/situation-page";
import { FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "How long is the cooling-off period in Queensland?",
    a: "For most residential purchases it is 5 business days. It starts on the day you, as buyer, receive a copy of the contract signed by both parties, and ends at 5pm on the fifth business day. Weekends and public holidays don't count.",
  },
  {
    q: "Does the seller get a cooling-off period?",
    a: "No. Cooling-off is a buyer's right only. Once the seller has signed, they are bound.",
  },
  {
    q: "What does it cost to pull out during cooling-off?",
    a: "The seller can keep a termination penalty of 0.25% of the purchase price, taken from your deposit. On a $700,000 home that is $1,750. The rest of the deposit is refunded.",
  },
  {
    q: "When is there no cooling-off period?",
    a: "There is none if you buy at auction, or if you sign after the property was passed in at auction on the same day. It can also be waived or shortened if your lawyer signs a certificate before you sign, which buyers sometimes do to win a competitive sale. Don't agree to waive it without advice.",
  },
  {
    q: "How do I end the contract during cooling-off?",
    a: "By giving the seller or their agent written notice before the period ends. It needs to be done properly and on time. If you're unsure, call us and we'll send the notice for you.",
  },
  {
    q: "Does the cooling off period in QLD apply to land and units?",
    a: "Yes. The cooling-off period applies to residential property in Queensland, including houses, units, townhouses and vacant residential land. It does not generally apply to commercial property or to purchases at auction.",
  },
  {
    q: "What if the cooling-off period has already ended?",
    a: "You can no longer end the contract just by changing your mind. You may still have a finance or building and pest condition, or a right arising from a defect in the seller's disclosure. If not, the usual way out is a deed of rescission agreed with the seller. Get advice before telling the agent anything.",
  },
  {
    q: "Is cooling-off the same as a deed of rescission?",
    a: "No. Cooling-off lets the buyer end the contract alone, for a 0.25% penalty. A deed of rescission is an agreement by both buyer and seller to cancel the contract, and the deposit split is negotiated.",
  },
  {
    q: "I've signed. What should I use the cooling-off period for?",
    a: "Get your contract reviewed straight away, confirm finance with your lender, and book your building and pest inspection. Five business days goes quickly. We offer a free contract review and aim to turn it around the same or next business day.",
  },
];

export const Route = createFileRoute("/cooling-off-period-qld")({
  head: () => ({
    meta: [
      { title: fitTitle("Cooling Off Period QLD: 5 Business Days Explained | Coastside Law") },
      {
        name: "description",
        content:
          fitDescription("The cooling off period in QLD is 5 business days for residential buyers. The 0.25% penalty, auctions, waivers, and how to end a contract. Free contract review."),
      },
      { property: "og:title", content: fitTitle("Cooling off period QLD: 5 business days, explained") },
      {
        property: "og:description",
        content: fitDescription("5 business days, a 0.25% penalty and a few traps. What buyers need to know, from Coastside Law."),
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://coastsidelaw.com.au/cooling-off-period-qld" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/cooling-off-period-qld" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: () => (
    <SituationPage
      eyebrow="Buying property"
      title="Cooling off period QLD: how it works"
      lead="The cooling off period in QLD gives residential buyers 5 business days to pull out of a signed contract. You've signed and the clock is running. Here's how it works, what it costs to pull out, and what to do next."
      boxTitle="The short version"
      boxItems={[
        "5 business days, starting when you receive the contract signed by both parties",
        "Buyers only, sellers are bound once they sign",
        "Pulling out costs 0.25% of the price",
        "No cooling-off at auction, and it can be waived by lawyer's certificate",
        "Notice must be in writing and on time",
        "Missed it? A deed of rescission with the seller may still be possible",
      ]}
      faqs={FAQS}
      links={[
        { to: "/conveyancing", label: "Conveyancing" },
        { to: "/fees", label: "Our fixed fees" },
        { to: "/what-does-a-conveyancer-do-qld", label: "What does a conveyancer do?" },
        { to: "/guides/deed-of-rescission-queensland", label: "Deed of rescission explained" },
        { to: "/guides/cooling-off-period-queensland", label: "After signing: your first checks and deadlines" },
        { to: "/buyers-solicitor-qld", label: "Free buyer contract review" },
      ]}
      formHeading="Signed already? Send us your contract"
      ctaTitle="Signed already? Call us today"
      ctaBody={`Call ${FIRM.phone}. We'll review your contract free and tell you where you stand before cooling-off ends.`}
    />
  ),
});
